/**
 * Explicit mapping from the chart-tab's read-side `PatientDocument`
 * (`@/types/patient-chart-api`) to the documents-module's
 * `PatientDocumentModel` (`@/documents/types/document`).
 *
 * **Why this exists** (Samer PR #260 review §1):
 * the two types describe the same underlying wire record but live in
 * different type-spaces with slightly different strictness. Casting
 * via `unknown` would hide any future drift between them — if the
 * read-side type ever drops a field the mutation hook reads, the
 * unknown cast would silently let `undefined` flow into the POST
 * body. A PHI-touching mutation can't have that risk.
 *
 * **What this maps**:
 * Every field the row-action hooks (`useMarkDocumentReviewed`,
 * `useArchiveDocument`) overlay onto the wire body — explicit field
 * list, no spread. Audit fields and the file array round-trip
 * verbatim. If the read-side type adds a new field the mutation
 * cares about, TypeScript will catch it at this mapping function,
 * not silently on the wire.
 *
 * **Fields read by the mutation hooks** (cross-referenced against
 * `use-document-row-actions.ts`):
 *   - `id` — document GUID (for backend update-vs-create branch).
 *   - `patientId` — PHI ownership.
 *   - `encounterId` — encounter scope.
 *   - `name`, `type`, `documentDate`, `doctorName`, `facilityName`,
 *     `tags` — content fields, round-tripped.
 *   - `reviewedById`, `reviewedByName`, `reviewedOn` — review audit;
 *     `useMarkDocumentReviewed` overlays these.
 *   - `createdById`, `createdByName`, `createdOn`, `updatedById`,
 *     `updatedByName`, `updatedOn` — creation/update audit; backend
 *     preserves on re-save when present.
 *   - `isDeleted` — soft-delete flag; `useArchiveDocument` overlays
 *     to `true`.
 *   - `patientDocumentFiles` — file attachments array.
 *
 * **Fields NOT on the read-side type but present on `PatientDocumentModel`**:
 *   - `data` — Procedure / Radiology types use this for the picked
 *     code + title. The chart-tab read-side `PatientDocument` doesn't
 *     surface it because the table doesn't render it. On the wire it
 *     ships back to the backend as `null` for chart-tab-edited
 *     documents, which is fine — those types are uploaded from the
 *     full document module, not chart-tab. If the user edits a
 *     Procedure / Radiology document via the chart-tab row actions,
 *     the existing `data` is LOST on the round-trip. Document this
 *     limitation in the JSDoc; if it becomes a problem, surface
 *     `data` on the chart-tab read type and round-trip it here.
 *   - `patientDocumentLabs` — same story as `data`. Lab-type
 *     documents have nested lab rows; chart-tab doesn't surface them.
 *     Round-trip as `[]` here. **Andrew + Samer PR #260 review §1
 *     follow-up**: track this limitation under PROD-842 cleanup
 *     candidates if Lab-type document edits from chart-tab become
 *     a real use case.
 */

import type { PatientDocumentModel } from '@/documents/types/document';
import type { PatientDocument } from '@/types/patient-chart-api';

export function patientDocumentToModel(
  d: PatientDocument,
): PatientDocumentModel {
  return {
    id: d.id ?? null,
    patientId: d.patientId ?? null,
    encounterId: d.encounterId ?? null,
    name: d.name ?? null,
    type: d.type ?? null,
    documentDate: d.documentDate ?? null,
    doctorName: d.doctorName ?? null,
    facilityName: d.facilityName ?? null,
    tags: d.tags ?? null,
    reviewedById: d.reviewedById ?? null,
    reviewedByName: d.reviewedByName ?? null,
    reviewedOn: d.reviewedOn ?? null,
    // PROD-1769 — these two used to be hardcoded `null` / `[]` here, on the
    // stated reasoning that "the backend re-shapes / merges on its end so
    // unedited rows don't lose state". That reasoning was wrong on three counts:
    //
    //  1. The backend maps `data` through `CustomObjectToJsonResolver`, which
    //     returns `string.Empty` when the property is absent — there is no
    //     keep-existing branch — onto a scalar column, saved via `Update()` on a
    //     detached graph where every scalar is marked Modified. So archiving a
    //     Procedure / Radiology document BLANKED its stored CPT code. Invisible
    //     in the UI, because the visible title survives separately via `name`.
    //  2. This repo's own docs/CONVERSIONS/documents-edit-popup/
    //     VERIFICATION_REPORT.md documents whole-list tombstone/replace
    //     semantics for `patientDocumentFiles` — the opposite of merging.
    //  3. It contradicted itself: the file array below is round-tripped
    //     explicitly, which would be pointless if arrays merged.
    //
    // Both values are present in the GET response — the read type had simply
    // narrowed them away (see PatientDocument in patient-chart-api.ts). Now
    // round-tripped. The `?? null` / `?? []` fallbacks keep the keys PRESENT on
    // the wire for a genuinely absent input, per the repo's nullable-field rule.
    data: d.data ?? null,
    createdById: d.createdById ?? null,
    createdByName: d.createdByName ?? null,
    createdOn: d.createdOn ?? null,
    updatedById: d.updatedById ?? null,
    updatedByName: d.updatedByName ?? null,
    updatedOn: d.updatedOn ?? null,
    isDeleted: d.isDeleted,
    patientDocumentFiles: (d.patientDocumentFiles ?? []).map((f) => ({
      id: f.id ?? null,
      patientDocumentId: f.patientDocumentId ?? null,
      url: f.url ?? null,
      fileName: f.fileName ?? null,
      fileType: f.fileType ?? null,
      isDeleted: f.isDeleted ?? false,
    })),
    patientDocumentLabs: (d.patientDocumentLabs ?? []).map((l) => ({
      id: l.id ?? null,
      patientDocumentId: l.patientDocumentId ?? null,
      labName: l.labName ?? null,
      result: l.result ?? null,
      unitType: l.unitType ?? null,
      isDeleted: l.isDeleted ?? false,
    })),
  };
}
